<script setup>
// WhatsApp Business araclari: isletme hesaplari, numaralar, sablonlar,
// akislar, QR kodlar ve analitik.
//
// Bu araclar reklam hesabina degil WhatsApp Business hesabina (WABA) ve
// telefon numarasina bagli calisir; o yuzden sayfanin kendi secicileri var ve
// ustteki reklam hesabi secimi burada kullanilmaz. Alt sekme yerine tek
// sayfada ic secici tutuldu; kenar menusu ve ust sekmeler zaten dolu.
import { computed, ref, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import Button from 'dashboard/components-next/button/Button.vue';
import { useAlert } from 'dashboard/composables';
import { aracCalistir, diziyeCevir } from './kopru.js';
import AracTablosu from './AracTablosu.vue';

defineProps({
  hesap: { type: String, default: '' },
  hesapAyrinti: { type: Object, default: null },
});

const { t } = useI18n();
const route = useRoute();

const hesapKimligi = computed(() => route.params.accountId);

const gorunum = ref('hesaplar');
const hata = ref('');
const yukleniyor = ref(false);

const gorunumler = computed(() => [
  { anahtar: 'hesaplar', ad: t('META_ADS.AYARLAR.WHATSAPP.GORUNUM_HESAPLAR') },
  { anahtar: 'numaralar', ad: t('META_ADS.AYARLAR.WHATSAPP.GORUNUM_NUMARALAR') },
  { anahtar: 'sablonlar', ad: t('META_ADS.AYARLAR.WHATSAPP.GORUNUM_SABLONLAR') },
  { anahtar: 'akislar', ad: t('META_ADS.AYARLAR.WHATSAPP.GORUNUM_AKISLAR') },
  { anahtar: 'qr', ad: t('META_ADS.AYARLAR.WHATSAPP.GORUNUM_QR') },
  { anahtar: 'analitik', ad: t('META_ADS.AYARLAR.WHATSAPP.GORUNUM_ANALITIK') },
]);

const hataYaz = e => {
  hata.value = e.response?.data?.error || e.message;
};

// --- Isletme hesaplari (WABA) ve numaralar --------------------------------
const wabalar = ref([]);
const seciliWaba = ref('');
const numaralar = ref([]);
const seciliNumara = ref('');

const wabaSutunlari = computed(() => [
  { anahtar: 'name', baslik: t('META_ADS.AYARLAR.WHATSAPP.AD') },
  { anahtar: 'id', baslik: t('META_ADS.AYARLAR.WHATSAPP.KIMLIK') },
  { anahtar: 'currency', baslik: t('META_ADS.AYARLAR.WHATSAPP.PARA_BIRIMI') },
  { anahtar: 'timezone_id', baslik: t('META_ADS.AYARLAR.WHATSAPP.SAAT_DILIMI') },
]);

const numaraSutunlari = computed(() => [
  {
    anahtar: 'display_phone_number',
    baslik: t('META_ADS.AYARLAR.WHATSAPP.NUMARA'),
    bicim: (deger, satir) => deger || satir.phone_number || '—',
  },
  { anahtar: 'verified_name', baslik: t('META_ADS.AYARLAR.WHATSAPP.DOGRULANMIS_AD') },
  { anahtar: 'id', baslik: t('META_ADS.AYARLAR.WHATSAPP.KIMLIK') },
  {
    anahtar: 'quality_rating',
    baslik: t('META_ADS.AYARLAR.WHATSAPP.KALITE'),
    bicim: (deger, satir) => deger || satir.status || '—',
  },
]);

const wabalariYukle = async () => {
  yukleniyor.value = true;
  hata.value = '';
  try {
    const sonuc = await aracCalistir(hesapKimligi.value, 'whatsapp_get_business_accounts');
    wabalar.value = diziyeCevir(sonuc);
    if (!seciliWaba.value && wabalar.value.length) {
      seciliWaba.value = wabalar.value[0].id || '';
    }
  } catch (e) {
    hataYaz(e);
  } finally {
    yukleniyor.value = false;
  }
};

const numaralariYukle = async () => {
  if (!seciliWaba.value) return;
  yukleniyor.value = true;
  hata.value = '';
  try {
    const sonuc = await aracCalistir(hesapKimligi.value, 'whatsapp_get_phone_numbers', {
      waba_id: seciliWaba.value,
    });
    numaralar.value = diziyeCevir(sonuc);
    const gecerli = numaralar.value.some(n => n.id === seciliNumara.value);
    if (!gecerli) seciliNumara.value = numaralar.value[0]?.id || '';
  } catch (e) {
    hataYaz(e);
  } finally {
    yukleniyor.value = false;
  }
};

// --- Isletme profili ------------------------------------------------------
const profil = ref(null);
const profilKaydediliyor = ref(false);

const profiliYukle = async () => {
  if (!seciliNumara.value) return;
  yukleniyor.value = true;
  hata.value = '';
  try {
    const sonuc = await aracCalistir(hesapKimligi.value, 'whatsapp_get_business_profile', {
      phone_number_id: seciliNumara.value,
    });
    const veri = Array.isArray(sonuc.veri) ? sonuc.veri[0] : sonuc.veri;
    profil.value = {
      about: veri?.about || '',
      description: veri?.description || '',
      email: veri?.email || '',
      address: veri?.address || '',
    };
  } catch (e) {
    hataYaz(e);
  } finally {
    yukleniyor.value = false;
  }
};

const profiliKaydet = async () => {
  if (!seciliNumara.value || !profil.value) return;
  // Profil metni musteriye WhatsApp'ta gorunur; yayina giden bir degisiklik
  // oldugu icin once onay aliniyor.
  if (!window.confirm(t('META_ADS.AYARLAR.WHATSAPP.PROFIL_KAYDETME_ONAYI'))) return;
  profilKaydediliyor.value = true;
  hata.value = '';
  try {
    // Bos birakilan alanlari gondermiyoruz: Meta gonderilen alani oldugu gibi
    // yazar, bos dize gonderirsek kullanicinin mevcut metnini silmis oluruz.
    const argumanlar = { phone_number_id: seciliNumara.value };
    Object.entries(profil.value).forEach(([anahtar, deger]) => {
      if (String(deger).trim()) argumanlar[anahtar] = String(deger).trim();
    });
    await aracCalistir(
      hesapKimligi.value,
      'whatsapp_update_business_profile',
      argumanlar,
      { onaylandi: true }
    );
    useAlert(t('META_ADS.AYARLAR.WHATSAPP.PROFIL_KAYDEDILDI'));
  } catch (e) {
    hataYaz(e);
  } finally {
    profilKaydediliyor.value = false;
  }
};

// --- Sablonlar ------------------------------------------------------------
const sablonlar = ref([]);
const yeniSablon = ref({ name: '', language: 'tr', category: 'MARKETING', govde: '' });
const duzenlenen = ref(null);
const duzenlenenGovde = ref('');
const sablonKaydediliyor = ref(false);

const sablonSutunlari = computed(() => [
  { anahtar: 'name', baslik: t('META_ADS.AYARLAR.WHATSAPP.SABLON_ADI') },
  { anahtar: 'language', baslik: t('META_ADS.AYARLAR.WHATSAPP.DIL') },
  { anahtar: 'category', baslik: t('META_ADS.AYARLAR.WHATSAPP.KATEGORI') },
  { anahtar: 'status', baslik: t('META_ADS.AYARLAR.WHATSAPP.DURUM') },
]);

const sablonlariYukle = async () => {
  if (!seciliWaba.value) return;
  yukleniyor.value = true;
  hata.value = '';
  try {
    const sonuc = await aracCalistir(hesapKimligi.value, 'whatsapp_get_templates', {
      waba_id: seciliWaba.value,
    });
    sablonlar.value = diziyeCevir(sonuc);
  } catch (e) {
    hataYaz(e);
  } finally {
    yukleniyor.value = false;
  }
};

const sablonOlustur = async () => {
  if (!seciliWaba.value || !yeniSablon.value.name.trim()) return;
  // Sablon Meta incelemesine gider ve adi bir daha degistirilemez; yanlis
  // isimle gonderilen sablon hesapta kalici cop birakir.
  if (!window.confirm(t('META_ADS.AYARLAR.WHATSAPP.SABLON_OLUSTURMA_ONAYI'))) return;
  sablonKaydediliyor.value = true;
  hata.value = '';
  try {
    await aracCalistir(
      hesapKimligi.value,
      'whatsapp_create_template',
      {
        waba_id: seciliWaba.value,
        name: yeniSablon.value.name.trim(),
        language: yeniSablon.value.language.trim(),
        category: yeniSablon.value.category,
        // Meta sablon govdesini bilesen dizisi olarak bekler; en yalin hali
        // tek bir BODY bileseni.
        components: [{ type: 'BODY', text: yeniSablon.value.govde }],
      },
      { onaylandi: true }
    );
    yeniSablon.value = { name: '', language: 'tr', category: 'MARKETING', govde: '' };
    useAlert(t('META_ADS.AYARLAR.WHATSAPP.SABLON_OLUSTURULDU'));
    await sablonlariYukle();
  } catch (e) {
    hataYaz(e);
  } finally {
    sablonKaydediliyor.value = false;
  }
};

const duzenlemeyiAc = sablon => {
  duzenlenen.value = sablon;
  const govde = (sablon.components || []).find(b => b.type === 'BODY');
  duzenlenenGovde.value = govde?.text || '';
};

const sablonGuncelle = async () => {
  if (!duzenlenen.value) return;
  // Onayli bir sablonun govdesi degisince Meta sablonu yeniden incelemeye alir;
  // o sure boyunca sablonla mesaj gonderilemez.
  if (!window.confirm(t('META_ADS.AYARLAR.WHATSAPP.SABLON_GUNCELLEME_ONAYI'))) return;
  sablonKaydediliyor.value = true;
  hata.value = '';
  try {
    await aracCalistir(
      hesapKimligi.value,
      'whatsapp_update_template',
      {
        waba_id: seciliWaba.value,
        template_id: duzenlenen.value.id,
        components: [{ type: 'BODY', text: duzenlenenGovde.value }],
      },
      { onaylandi: true }
    );
    duzenlenen.value = null;
    useAlert(t('META_ADS.AYARLAR.WHATSAPP.SABLON_GUNCELLENDI'));
    await sablonlariYukle();
  } catch (e) {
    hataYaz(e);
  } finally {
    sablonKaydediliyor.value = false;
  }
};

const sablonSil = async sablon => {
  // Silinen sablon geri gelmez ve onayli sablonun yeniden onaylanmasi gunler
  // surebilir; once soruyoruz.
  const soru = t('META_ADS.AYARLAR.WHATSAPP.SABLON_SILME_ONAYI', {
    ad: sablon.name || sablon.id,
  });
  if (!window.confirm(soru)) return;
  hata.value = '';
  try {
    await aracCalistir(
      hesapKimligi.value,
      'whatsapp_delete_template',
      { waba_id: seciliWaba.value, name: sablon.name || sablon.id },
      { onaylandi: true }
    );
    useAlert(t('META_ADS.AYARLAR.WHATSAPP.SABLON_SILINDI'));
    await sablonlariYukle();
  } catch (e) {
    hataYaz(e);
  }
};

// --- Akislar --------------------------------------------------------------
const akislar = ref([]);

const akisSutunlari = computed(() => [
  { anahtar: 'name', baslik: t('META_ADS.AYARLAR.WHATSAPP.AKIS_ADI') },
  { anahtar: 'id', baslik: t('META_ADS.AYARLAR.WHATSAPP.KIMLIK') },
  { anahtar: 'status', baslik: t('META_ADS.AYARLAR.WHATSAPP.DURUM') },
  { anahtar: 'categories', baslik: t('META_ADS.AYARLAR.WHATSAPP.KATEGORI') },
]);

const akislariYukle = async () => {
  if (!seciliWaba.value) return;
  yukleniyor.value = true;
  hata.value = '';
  try {
    const sonuc = await aracCalistir(hesapKimligi.value, 'whatsapp_get_flows', {
      waba_id: seciliWaba.value,
    });
    akislar.value = diziyeCevir(sonuc);
  } catch (e) {
    hataYaz(e);
  } finally {
    yukleniyor.value = false;
  }
};

// --- QR kodlar ------------------------------------------------------------
const qrKodlar = ref([]);
const yeniQrMesaji = ref('');
const qrKaydediliyor = ref(false);

const qrSutunlari = computed(() => [
  {
    anahtar: 'code',
    baslik: t('META_ADS.AYARLAR.WHATSAPP.QR_KODU'),
    bicim: (deger, satir) => deger || satir.id || '—',
  },
  {
    anahtar: 'prefilled_message',
    baslik: t('META_ADS.AYARLAR.WHATSAPP.ON_MESAJ'),
  },
  {
    anahtar: 'deep_link_url',
    baslik: t('META_ADS.AYARLAR.WHATSAPP.BAGLANTI'),
  },
]);

const qrYukle = async () => {
  if (!seciliNumara.value) return;
  yukleniyor.value = true;
  hata.value = '';
  try {
    const sonuc = await aracCalistir(hesapKimligi.value, 'whatsapp_get_qr_codes', {
      phone_number_id: seciliNumara.value,
    });
    qrKodlar.value = diziyeCevir(sonuc);
  } catch (e) {
    hataYaz(e);
  } finally {
    yukleniyor.value = false;
  }
};

const qrOlustur = async () => {
  if (!seciliNumara.value || !yeniQrMesaji.value.trim()) return;
  // Kod olusur olusmaz musteriye dagitilabilir hale gelir ve on mesaji sonradan
  // duzeltilemez; once soruluyor.
  if (!window.confirm(t('META_ADS.AYARLAR.WHATSAPP.QR_OLUSTURMA_ONAYI'))) return;
  qrKaydediliyor.value = true;
  hata.value = '';
  try {
    await aracCalistir(
      hesapKimligi.value,
      'whatsapp_create_qr_code',
      { phone_number_id: seciliNumara.value, prefilled_message: yeniQrMesaji.value.trim() },
      { onaylandi: true }
    );
    yeniQrMesaji.value = '';
    useAlert(t('META_ADS.AYARLAR.WHATSAPP.QR_OLUSTURULDU'));
    await qrYukle();
  } catch (e) {
    hataYaz(e);
  } finally {
    qrKaydediliyor.value = false;
  }
};

const qrSil = async kayit => {
  // Basili materyaldeki kod aninda calismaz hale gelir; onay sart.
  if (!window.confirm(t('META_ADS.AYARLAR.WHATSAPP.QR_SILME_ONAYI'))) return;
  hata.value = '';
  try {
    await aracCalistir(
      hesapKimligi.value,
      'whatsapp_delete_qr_code',
      { phone_number_id: seciliNumara.value, code: kayit.code || kayit.id },
      { onaylandi: true }
    );
    useAlert(t('META_ADS.AYARLAR.WHATSAPP.QR_SILINDI'));
    await qrYukle();
  } catch (e) {
    hataYaz(e);
  }
};

// --- Analitik -------------------------------------------------------------
const analitikOzeti = ref('');
const analitikSatirlari = ref([]);
const sablonAnalitigi = ref([]);
const abonelikler = ref([]);

const analitigiYukle = async () => {
  if (!seciliWaba.value) return;
  yukleniyor.value = true;
  hata.value = '';
  try {
    // Uc arac da ayni WABA'yi anlatiyor; tek gorunumde birlikte okunuyorlar.
    const [genel, sablon, webhook] = await Promise.all([
      aracCalistir(hesapKimligi.value, 'whatsapp_get_analytics', {
        waba_id: seciliWaba.value,
      }),
      aracCalistir(hesapKimligi.value, 'whatsapp_get_template_analytics', {
        waba_id: seciliWaba.value,
      }),
      aracCalistir(hesapKimligi.value, 'whatsapp_get_webhook_subscriptions', {
        waba_id: seciliWaba.value,
      }),
    ]);
    analitikOzeti.value = genel.ozet || '';
    analitikSatirlari.value = diziyeCevir(genel);
    sablonAnalitigi.value = diziyeCevir(sablon);
    abonelikler.value = diziyeCevir(webhook);
  } catch (e) {
    hataYaz(e);
  } finally {
    yukleniyor.value = false;
  }
};

// --- Gorunum yonetimi -----------------------------------------------------
// Her gorunum kendi verisini ilk acilisinda ceker; hepsini pesin yuklemek
// gereksiz yere onlarca Meta istegi demek olurdu.
const gorunumuYukle = () => {
  hata.value = '';
  if (gorunum.value === 'hesaplar') return wabalariYukle();
  if (gorunum.value === 'numaralar') return numaralariYukle();
  if (gorunum.value === 'sablonlar') return sablonlariYukle();
  if (gorunum.value === 'akislar') return akislariYukle();
  if (gorunum.value === 'qr') return qrYukle();
  if (gorunum.value === 'analitik') return analitigiYukle();
  return undefined;
};

const gorunumSec = anahtar => {
  gorunum.value = anahtar;
  gorunumuYukle();
};

// WABA degisince numara listesi eskir; once onu tazeleyip sonra gorunumu
// yeniliyoruz ki numara gerektiren araclar bos kimlikle cagrilmasin.
// "hesaplar" WABA listesinin kendisidir, "numaralar" da yukarida tazelendi;
// ikisini yeniden istemek bosuna Meta cagrisi olur.
watch(seciliWaba, async () => {
  await numaralariYukle();
  if (!['hesaplar', 'numaralar'].includes(gorunum.value)) gorunumuYukle();
});

watch(seciliNumara, () => {
  // Profil forma yuklenmis eski numaranin verisini tasimasin.
  profil.value = null;
  if (gorunum.value === 'qr') qrYukle();
});

// Numaralari burada cagirmiyoruz: ilk WABA secildigi anda yukaridaki izleyici
// zaten tetiklenir.
onMounted(wabalariYukle);
</script>

<template>
  <div class="flex flex-col gap-4 p-6">
    <!-- Ic secici: sekme yerine, ust sekme cubugunu ikinci kez doldurmamak icin -->
    <nav class="flex flex-wrap gap-1 p-1 rounded-lg bg-n-alpha-1 w-fit">
      <button
        v-for="g in gorunumler"
        :key="g.anahtar"
        type="button"
        class="px-3 py-1.5 text-sm rounded-md transition-colors"
        :class="
          gorunum === g.anahtar
            ? 'bg-n-surface-1 text-n-slate-12 font-medium'
            : 'text-n-slate-11 hover:text-n-slate-12'
        "
        @click="gorunumSec(g.anahtar)"
      >
        {{ g.ad }}
      </button>
    </nav>

    <div class="flex flex-wrap items-end gap-3">
      <label class="flex flex-col gap-1 text-xs text-n-slate-11">
        {{ t('META_ADS.AYARLAR.WHATSAPP.ISLETME_HESABI') }}
        <select v-model="seciliWaba" class="!mb-0 !w-auto min-w-[240px]">
          <option v-for="w in wabalar" :key="w.id" :value="w.id">
            {{ w.name || w.id }}
          </option>
        </select>
      </label>

      <label
        v-if="['numaralar', 'qr'].includes(gorunum)"
        class="flex flex-col gap-1 text-xs text-n-slate-11"
      >
        {{ t('META_ADS.AYARLAR.WHATSAPP.TELEFON_NUMARASI') }}
        <select v-model="seciliNumara" class="!mb-0 !w-auto min-w-[220px]">
          <option v-for="n in numaralar" :key="n.id" :value="n.id">
            {{ n.display_phone_number || n.phone_number || n.id }}
          </option>
        </select>
      </label>

      <Button
        variant="faded"
        size="sm"
        icon="i-lucide-refresh-cw"
        :label="t('META_ADS.AYARLAR.WHATSAPP.YENILE')"
        @click="gorunumuYukle"
      />
    </div>

    <p v-if="hata" class="text-sm text-n-ruby-11">{{ hata }}</p>

    <p v-if="!wabalar.length && !yukleniyor" class="text-sm text-n-slate-11">
      {{ t('META_ADS.AYARLAR.WHATSAPP.HESAP_YOK') }}
    </p>

    <!-- Hesaplar -->
    <section v-if="gorunum === 'hesaplar'" class="border rounded-lg border-n-weak">
      <AracTablosu
        :satirlar="wabalar"
        :sutunlar="wabaSutunlari"
        :yukleniyor="yukleniyor"
        :bos-metin="t('META_ADS.AYARLAR.WHATSAPP.HESAP_YOK')"
      />
    </section>

    <!-- Numaralar ve isletme profili -->
    <template v-if="gorunum === 'numaralar'">
      <section class="border rounded-lg border-n-weak">
        <AracTablosu
          :satirlar="numaralar"
          :sutunlar="numaraSutunlari"
          :yukleniyor="yukleniyor"
          :bos-metin="t('META_ADS.AYARLAR.WHATSAPP.NUMARA_YOK')"
        />
      </section>

      <section class="border rounded-lg border-n-weak">
        <header class="flex items-center gap-3 px-4 py-3 border-b border-n-weak">
          <h2 class="text-sm font-medium text-n-slate-12">
            {{ t('META_ADS.AYARLAR.WHATSAPP.ISLETME_PROFILI') }}
          </h2>
          <span class="grow" />
          <Button
            variant="faded"
            size="sm"
            :label="t('META_ADS.AYARLAR.WHATSAPP.PROFILI_GETIR')"
            :disabled="!seciliNumara"
            @click="profiliYukle"
          />
        </header>

        <div v-if="profil" class="grid grid-cols-2 gap-4 px-4 py-4">
          <label class="flex flex-col gap-1 text-xs text-n-slate-11">
            {{ t('META_ADS.AYARLAR.WHATSAPP.HAKKINDA') }}
            <input v-model="profil.about" type="text" class="!mb-0" />
          </label>
          <label class="flex flex-col gap-1 text-xs text-n-slate-11">
            {{ t('META_ADS.AYARLAR.WHATSAPP.ACIKLAMA') }}
            <input v-model="profil.description" type="text" class="!mb-0" />
          </label>
          <label class="flex flex-col gap-1 text-xs text-n-slate-11">
            {{ t('META_ADS.AYARLAR.WHATSAPP.EPOSTA') }}
            <input v-model="profil.email" type="email" class="!mb-0" />
          </label>
          <label class="flex flex-col gap-1 text-xs text-n-slate-11">
            {{ t('META_ADS.AYARLAR.WHATSAPP.ADRES') }}
            <input v-model="profil.address" type="text" class="!mb-0" />
          </label>
          <div class="col-span-2">
            <Button
              variant="solid"
              size="sm"
              :label="t('META_ADS.AYARLAR.WHATSAPP.PROFILI_KAYDET')"
              :disabled="profilKaydediliyor"
              @click="profiliKaydet"
            />
          </div>
        </div>

        <p v-else class="px-4 py-6 text-sm text-n-slate-11">
          {{ t('META_ADS.AYARLAR.WHATSAPP.PROFIL_GETIRILMEDI') }}
        </p>
      </section>
    </template>

    <!-- Sablonlar -->
    <template v-if="gorunum === 'sablonlar'">
      <section class="border rounded-lg border-n-weak">
        <AracTablosu
          :satirlar="sablonlar"
          :sutunlar="sablonSutunlari"
          :yukleniyor="yukleniyor"
          :bos-metin="t('META_ADS.AYARLAR.WHATSAPP.SABLON_YOK')"
        >
          <template #eylemler="{ satir }">
            <Button
              variant="faded"
              size="sm"
              :label="t('META_ADS.AYARLAR.WHATSAPP.DUZENLE')"
              @click="duzenlemeyiAc(satir)"
            />
            <Button
              variant="faded"
              size="sm"
              icon="i-lucide-trash-2"
              class="ml-2"
              :label="t('META_ADS.AYARLAR.WHATSAPP.SIL')"
              @click="sablonSil(satir)"
            />
          </template>
        </AracTablosu>
      </section>

      <section v-if="duzenlenen" class="border rounded-lg border-n-weak">
        <header class="px-4 py-3 border-b border-n-weak">
          <h2 class="text-sm font-medium text-n-slate-12">
            {{
              t('META_ADS.AYARLAR.WHATSAPP.SABLON_DUZENLE', {
                ad: duzenlenen.name || duzenlenen.id,
              })
            }}
          </h2>
        </header>
        <div class="flex flex-col gap-3 px-4 py-4">
          <label class="flex flex-col gap-1 text-xs text-n-slate-11">
            {{ t('META_ADS.AYARLAR.WHATSAPP.GOVDE') }}
            <textarea v-model="duzenlenenGovde" rows="3" class="!mb-0" />
          </label>
          <div class="flex gap-2">
            <Button
              variant="solid"
              size="sm"
              :label="t('META_ADS.AYARLAR.WHATSAPP.KAYDET')"
              :disabled="sablonKaydediliyor"
              @click="sablonGuncelle"
            />
            <Button
              variant="faded"
              size="sm"
              :label="t('META_ADS.AYARLAR.WHATSAPP.VAZGEC')"
              @click="duzenlenen = null"
            />
          </div>
        </div>
      </section>

      <section class="border rounded-lg border-n-weak">
        <header class="px-4 py-3 border-b border-n-weak">
          <h2 class="text-sm font-medium text-n-slate-12">
            {{ t('META_ADS.AYARLAR.WHATSAPP.YENI_SABLON') }}
          </h2>
        </header>
        <div class="flex flex-col gap-3 px-4 py-4">
          <div class="flex flex-wrap gap-3">
            <label class="flex flex-col gap-1 text-xs text-n-slate-11">
              {{ t('META_ADS.AYARLAR.WHATSAPP.SABLON_ADI') }}
              <input v-model="yeniSablon.name" type="text" class="!mb-0 w-56" />
            </label>
            <label class="flex flex-col gap-1 text-xs text-n-slate-11">
              {{ t('META_ADS.AYARLAR.WHATSAPP.DIL') }}
              <input v-model="yeniSablon.language" type="text" class="!mb-0 w-24" />
            </label>
            <label class="flex flex-col gap-1 text-xs text-n-slate-11">
              {{ t('META_ADS.AYARLAR.WHATSAPP.KATEGORI') }}
              <select v-model="yeniSablon.category" class="!mb-0 !w-auto">
                <option value="MARKETING">MARKETING</option>
                <option value="UTILITY">UTILITY</option>
                <option value="AUTHENTICATION">AUTHENTICATION</option>
              </select>
            </label>
          </div>
          <label class="flex flex-col gap-1 text-xs text-n-slate-11">
            {{ t('META_ADS.AYARLAR.WHATSAPP.GOVDE') }}
            <textarea v-model="yeniSablon.govde" rows="3" class="!mb-0" />
          </label>
          <div>
            <Button
              variant="solid"
              size="sm"
              icon="i-lucide-plus"
              :label="t('META_ADS.AYARLAR.WHATSAPP.SABLON_OLUSTUR')"
              :disabled="sablonKaydediliyor || !yeniSablon.name"
              @click="sablonOlustur"
            />
          </div>
        </div>
      </section>
    </template>

    <!-- Akislar -->
    <section v-if="gorunum === 'akislar'" class="border rounded-lg border-n-weak">
      <AracTablosu
        :satirlar="akislar"
        :sutunlar="akisSutunlari"
        :yukleniyor="yukleniyor"
        :bos-metin="t('META_ADS.AYARLAR.WHATSAPP.AKIS_YOK')"
      />
    </section>

    <!-- QR kodlar -->
    <template v-if="gorunum === 'qr'">
      <section class="border rounded-lg border-n-weak">
        <AracTablosu
          :satirlar="qrKodlar"
          :sutunlar="qrSutunlari"
          :yukleniyor="yukleniyor"
          :bos-metin="t('META_ADS.AYARLAR.WHATSAPP.QR_YOK')"
        >
          <template #eylemler="{ satir }">
            <Button
              variant="faded"
              size="sm"
              icon="i-lucide-trash-2"
              :label="t('META_ADS.AYARLAR.WHATSAPP.SIL')"
              @click="qrSil(satir)"
            />
          </template>
        </AracTablosu>
      </section>

      <section class="border rounded-lg border-n-weak">
        <div class="flex flex-wrap items-end gap-3 px-4 py-4">
          <label class="flex flex-col gap-1 text-xs text-n-slate-11 grow">
            {{ t('META_ADS.AYARLAR.WHATSAPP.ON_MESAJ') }}
            <input v-model="yeniQrMesaji" type="text" class="!mb-0 min-w-[280px]" />
          </label>
          <Button
            variant="solid"
            size="sm"
            icon="i-lucide-plus"
            :label="t('META_ADS.AYARLAR.WHATSAPP.QR_OLUSTUR')"
            :disabled="qrKaydediliyor || !seciliNumara || !yeniQrMesaji"
            @click="qrOlustur"
          />
        </div>
      </section>
    </template>

    <!-- Analitik -->
    <template v-if="gorunum === 'analitik'">
      <p v-if="analitikOzeti" class="text-sm whitespace-pre-line text-n-slate-11">
        {{ analitikOzeti }}
      </p>

      <section class="border rounded-lg border-n-weak">
        <header class="px-4 py-3 border-b border-n-weak">
          <h2 class="text-sm font-medium text-n-slate-12">
            {{ t('META_ADS.AYARLAR.WHATSAPP.MESAJ_ANALITIGI') }}
          </h2>
        </header>
        <AracTablosu
          :satirlar="analitikSatirlari"
          :yukleniyor="yukleniyor"
          :bos-metin="t('META_ADS.AYARLAR.WHATSAPP.ANALITIK_YOK')"
        />
      </section>

      <section class="border rounded-lg border-n-weak">
        <header class="px-4 py-3 border-b border-n-weak">
          <h2 class="text-sm font-medium text-n-slate-12">
            {{ t('META_ADS.AYARLAR.WHATSAPP.SABLON_ANALITIGI') }}
          </h2>
        </header>
        <AracTablosu
          :satirlar="sablonAnalitigi"
          :yukleniyor="yukleniyor"
          :bos-metin="t('META_ADS.AYARLAR.WHATSAPP.ANALITIK_YOK')"
        />
      </section>

      <section class="border rounded-lg border-n-weak">
        <header class="px-4 py-3 border-b border-n-weak">
          <h2 class="text-sm font-medium text-n-slate-12">
            {{ t('META_ADS.AYARLAR.WHATSAPP.WEBHOOK_ABONELIKLERI') }}
          </h2>
        </header>
        <AracTablosu
          :satirlar="abonelikler"
          :yukleniyor="yukleniyor"
          :bos-metin="t('META_ADS.AYARLAR.WHATSAPP.ABONELIK_YOK')"
        />
      </section>
    </template>
  </div>
</template>
